import { GridFilter } from "../types";
import {
  calendarDayFilter,
  calendarFromToFilter,
  calendarYearFilter,
  calendarYearMonthFilter,
} from "../../../common/services/dateFilterCore";

/**
 * Mirrors `filterValues2Expression` (entities/common/services/filterService.ts),
 * which turns the same operations into the Prisma filter the server receives.
 * Both switches must cover the same operation names: an operation handled there
 * and missing here silently stops filtering a cached list.
 */

type FilterFunction = (item: any) => boolean;

const getNestedValue = (obj: any, path: string): any => {
  const parts = path.split(".");
  let value = obj;
  for (const part of parts) {
    if (value == null) return undefined;
    value = value[part];
  }
  return value;
};

const normalizeString = (value: any): string => {
  if (value == null) return "";
  return String(value).toLowerCase();
};

const dayFilter = (columnName: string, value: string, _timeZone: string): FilterFunction => {
  const [year, month, day] = value.replaceAll("/", "-").split("-").map(x => x?.trim());
  if (!year || !month || !day) return () => true;

  const fromStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const from = new Date(fromStr);
  const to = new Date(from);
  to.setDate(from.getDate() + 1);

  return (item: any) => {
    const itemValue = getNestedValue(item, columnName);
    if (!itemValue) return false;
    const itemDate = new Date(itemValue);
    return itemDate >= from && itemDate < to;
  };
};

const yearFilter = (columnName: string, value: string): FilterFunction => {
  const year = parseInt(value.trim(), 10);
  if (isNaN(year)) return () => true;

  const from = new Date(`${year}-01-01`);
  const to = new Date(`${year + 1}-01-01`);

  return (item: any) => {
    const itemValue = getNestedValue(item, columnName);
    if (!itemValue) return false;
    const itemDate = new Date(itemValue);
    return itemDate >= from && itemDate < to;
  };
};

const yearMonthFilter = (columnName: string, value: string): FilterFunction => {
  const [year, month] = value.replaceAll("/", "-").split("-").map(x => x?.trim());
  if (!year || !month) return () => true;

  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const from = new Date(`${y}-${m.toString().padStart(2, '0')}-01`);
  const to = m === 12
    ? new Date(`${y + 1}-01-01`)
    : new Date(`${y}-${(m + 1).toString().padStart(2, '0')}-01`);

  return (item: any) => {
    const itemValue = getNestedValue(item, columnName);
    if (!itemValue) return false;
    const itemDate = new Date(itemValue);
    return itemDate >= from && itemDate < to;
  };
};

const fromToFilter = (columnName: string, value: string, _timeZone: string): FilterFunction => {
  const [fromStr, toStr] = value.split(",").map(x => x?.trim().replaceAll("/", "-"));
  if (!fromStr || !toStr) return () => true;

  const from = new Date(fromStr);
  const to = new Date(toStr);
  to.setDate(to.getDate() + 1); // Include the end date

  return (item: any) => {
    const itemValue = getNestedValue(item, columnName);
    if (!itemValue) return false;
    const itemDate = new Date(itemValue);
    return itemDate >= from && itemDate < to;
  };
};

const createFilterFunction = (
  columnName: string,
  columnType: string,
  operation: string,
  value: any,
  timeZone: string,
  subFilters: Record<string, { subFields: string[]; multi: boolean } | undefined>
): FilterFunction => {
  // Parse value for Int type
  if (columnType === 'Int') {
    value = parseInt(value, 10);
  }

  // Un date (día de calendario, "AAAA-MM-DD"): el mismo filtro que manda
  // filterValues2Expression, comparado como texto (matchPrismaFilter). Un valor que
  // no es un día no filtra, como allá.
  if (columnType === "Date") {
    const calendar =
      operation === "day" ? calendarDayFilter(columnName, String(value)) :
      operation === "year" ? calendarYearFilter(columnName, String(value)) :
      operation === "year-month" ? calendarYearMonthFilter(columnName, String(value)) :
      operation === "from-to" ? calendarFromToFilter(columnName, String(value)) :
      undefined;
    if (calendar !== undefined) {
      return calendar ? (item) => matchPrismaFilter(item, calendar) : () => true;
    }
  }

  // Handle reference filters (displayId matching)
  const filterSubFields = subFilters[columnName];

  const matchValue = (itemValue: any, op: string, filterValue: any): boolean => {
    if (itemValue == null) return false;

    const strItem = normalizeString(itemValue);
    const strFilter = normalizeString(filterValue);

    switch (op) {
      case "contains":
        return strItem.includes(strFilter);
      case "startsWith":
        return strItem.startsWith(strFilter);
      case "endsWith":
        return strItem.endsWith(strFilter);
      case "equals":
      case "equals-Str":
        return strItem === strFilter;
      default:
        return strItem.includes(strFilter);
    }
  };

  const matchSubFields = (item: any, op: string): boolean => {
    if (!filterSubFields) {
      const itemValue = getNestedValue(item, columnName);
      return matchValue(itemValue, op, value);
    }

    const columnValue = getNestedValue(item, columnName);
    if (!columnValue) return false;

    // Handle multi (array of references)
    if (filterSubFields.multi && Array.isArray(columnValue)) {
      return columnValue.some((subItem: any) =>
        filterSubFields.subFields.some(subField =>
          matchValue(getNestedValue(subItem, subField), op, value)
        )
      );
    }

    // Single reference
    return filterSubFields.subFields.some(subField =>
      matchValue(getNestedValue(columnValue, subField), op, value)
    );
  };

  switch (operation) {
    case "contains":
      return (item) => matchSubFields(item, "contains");
    case "startsWith":
      return (item) => matchSubFields(item, "startsWith");
    case "endsWith":
      return (item) => matchSubFields(item, "endsWith");
    case "notContains":
      return (item) => !matchSubFields(item, "contains");
    case "equalStr":
      return (item) => matchSubFields(item, "equals-Str");
    case "notEqualStr":
      return (item) => !matchSubFields(item, "equals-Str");
    case "equal":
      return (item) => matchSubFields(item, "equals");
    case "notEqual":
      return (item) => !matchSubFields(item, "equals");
    case "lessThan":
      return (item) => {
        const itemValue = getNestedValue(item, columnName);
        return itemValue != null && itemValue < value;
      };
    case "lessThanOrEqual":
      return (item) => {
        const itemValue = getNestedValue(item, columnName);
        return itemValue != null && itemValue <= value;
      };
    case "greaterThan":
      return (item) => {
        const itemValue = getNestedValue(item, columnName);
        return itemValue != null && itemValue > value;
      };
    case "greaterThanOrEqual":
      return (item) => {
        const itemValue = getNestedValue(item, columnName);
        return itemValue != null && itemValue >= value;
      };
    case "day":
      return dayFilter(columnName, value, timeZone);
    case "year":
      return yearFilter(columnName, value);
    case "year-month":
      return yearMonthFilter(columnName, value);
    case "from-to":
      return fromToFilter(columnName, value, timeZone);
    case "empty":
      return (item) => getNestedValue(item, columnName) == null;
    case "notEmpty":
      return (item) => getNestedValue(item, columnName) != null;
    case "yes":
      return (item) => getNestedValue(item, columnName) === true;
    case "no":
      return (item) => getNestedValue(item, columnName) === false;
    case "all":
      return () => true;
    default:
      return () => true;
  }
};

export const applyClientFilters = <T>(
  data: T[],
  filters: GridFilter[],
  columnTypes: Record<string, string>,
  timeZone: string,
  subFilters: Record<string, { subFields: string[]; multi: boolean } | undefined>
): T[] => {
  if (!filters || filters.length === 0) return data;

  // "empty" y "notEmpty" no llevan valor: valen aunque venga vacío.
  const valueless = (f: GridFilter) => f.operation === "empty" || f.operation === "notEmpty";
  const filterFunctions = filters
    .filter(f => valueless(f) || (f.value != null && f.value !== ""))
    .map(filter =>
      createFilterFunction(
        filter.columnName,
        columnTypes[filter.columnName] || "String",
        filter.operation || "contains",
        filter.value,
        timeZone,
        subFilters
      )
    );

  if (filterFunctions.length === 0) return data;

  return data.filter(item =>
    filterFunctions.every(fn => fn(item))
  );
};

export const applyClientSearch = <T>(
  data: T[],
  searchValue: string,
  searchFields: string[]
): T[] => {
  if (!searchValue || searchValue.trim() === "") return data;

  const searchLower = searchValue.toLowerCase().trim();

  return data.filter(item =>
    searchFields.some(field => {
      const value = getNestedValue(item, field);
      if (value == null) return false;
      return String(value).toLowerCase().includes(searchLower);
    })
  );
};

export const applyClientSort = <T>(
  data: T[],
  sorting: { columnName: string; direction: "asc" | "desc" }[]
): T[] => {
  if (!sorting || sorting.length === 0) return data;

  return [...data].sort((a, b) => {
    for (const sort of sorting) {
      const aValue = getNestedValue(a, sort.columnName);
      const bValue = getNestedValue(b, sort.columnName);

      let comparison = 0;
      if (aValue == null && bValue == null) comparison = 0;
      else if (aValue == null) comparison = 1;
      else if (bValue == null) comparison = -1;
      else if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      if (comparison !== 0) {
        return sort.direction === "desc" ? -comparison : comparison;
      }
    }
    return 0;
  });
};

export const applyClientPagination = <T>(
  data: T[],
  page: number,
  pageSize: number
): { items: T[]; hasMore: boolean; total: number } => {
  const start = (page - 1) * pageSize;
  const items = data.slice(start, start + pageSize);
  return {
    items,
    hasMore: start + pageSize < data.length,
    total: data.length,
  };
};

/**
 * Apply Prisma WHERE-style filters to data client-side.
 * Supports: contains, startsWith, endsWith, equals, gt, gte, lt, lte
 */
export const applyPrismaWhereFilters = <T>(
  data: T[],
  whereFilters: any[] | undefined
): T[] => {
  if (!whereFilters || whereFilters.length === 0) return data;

  return data.filter(item => {
    return whereFilters.every(filterObj => matchPrismaFilter(item, filterObj));
  });
};

const matchPrismaFilter = (item: any, filterObj: any): boolean => {
  if (filterObj.AND) {
    return filterObj.AND.every((f: any) => matchPrismaFilter(item, f));
  }

  if (filterObj.OR) {
    return filterObj.OR.some((f: any) => matchPrismaFilter(item, f));
  }

  for (const fieldName of Object.keys(filterObj)) {
    const condition = filterObj[fieldName];
    const itemValue = getNestedValue(item, fieldName);

    if (!matchFieldCondition(itemValue, condition)) {
      return false;
    }
  }

  return true;
};

/**
 * Extract all string values from a value (handles multilanguage objects).
 * For { es: "SADAIC", en: "SADAIC" } returns ["SADAIC", "SADAIC"]
 * For "simple string" returns ["simple string"]
 */
const extractStringValues = (value: any): string[] => {
  if (value == null) return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (typeof value === 'object' && !Array.isArray(value)) {
    // Multilanguage object: extract all string values
    return Object.values(value).filter((v): v is string => typeof v === 'string');
  }
  return [];
};

const matchFieldCondition = (itemValue: any, condition: any): boolean => {
  if (condition == null) return true;

  if (typeof condition === 'object' && !Array.isArray(condition)) {
    // Handle 'some' operator for array fields (many-to-many relations)
    if ('some' in condition) {
      if (!Array.isArray(itemValue)) return false;
      return itemValue.some((element: any) => matchPrismaFilter(element, condition.some));
    }

    // Handle 'every' operator for array fields
    if ('every' in condition) {
      if (!Array.isArray(itemValue)) return false;
      return itemValue.every((element: any) => matchPrismaFilter(element, condition.every));
    }

    // Handle 'none' operator for array fields
    if ('none' in condition) {
      if (!Array.isArray(itemValue)) return true;
      return !itemValue.some((element: any) => matchPrismaFilter(element, condition.none));
    }

    const isInsensitive = condition.mode === 'insensitive';

    if ('contains' in condition) {
      if (itemValue == null) return false;
      const searchVal = String(condition.contains);
      const itemStrings = extractStringValues(itemValue);
      if (itemStrings.length === 0) return false;
      return itemStrings.some(itemStr =>
        isInsensitive
          ? itemStr.toLowerCase().includes(searchVal.toLowerCase())
          : itemStr.includes(searchVal)
      );
    }

    if ('startsWith' in condition) {
      if (itemValue == null) return false;
      const searchVal = String(condition.startsWith);
      const itemStrings = extractStringValues(itemValue);
      if (itemStrings.length === 0) return false;
      return itemStrings.some(itemStr =>
        isInsensitive
          ? itemStr.toLowerCase().startsWith(searchVal.toLowerCase())
          : itemStr.startsWith(searchVal)
      );
    }

    if ('endsWith' in condition) {
      if (itemValue == null) return false;
      const searchVal = String(condition.endsWith);
      const itemStrings = extractStringValues(itemValue);
      if (itemStrings.length === 0) return false;
      return itemStrings.some(itemStr =>
        isInsensitive
          ? itemStr.toLowerCase().endsWith(searchVal.toLowerCase())
          : itemStr.endsWith(searchVal)
      );
    }

    // { not: null }: la columna con valor ("No vacío"). Sin esta rama caía en la
    // comparación de abajo y no dejaba pasar ninguna fila.
    if ('not' in condition && condition.not == null) {
      return itemValue != null;
    }

    if ('equals' in condition) {
      if (condition.equals == null) return itemValue == null;
      if (itemValue == null) return false;
      const searchVal = String(condition.equals);
      const itemStrings = extractStringValues(itemValue);
      if (itemStrings.length === 0) return false;
      return itemStrings.some(itemStr =>
        isInsensitive
          ? itemStr.toLowerCase() === searchVal.toLowerCase()
          : itemStr === searchVal
      );
    }

    if ('gt' in condition) {
      return itemValue != null && itemValue > condition.gt;
    }
    if ('gte' in condition) {
      return itemValue != null && itemValue >= condition.gte;
    }
    if ('lt' in condition) {
      return itemValue != null && itemValue < condition.lt;
    }
    if ('lte' in condition) {
      return itemValue != null && itemValue <= condition.lte;
    }

    if (typeof itemValue === 'object' && itemValue !== null) {
      return matchPrismaFilter(itemValue, condition);
    }
  }

  return itemValue === condition;
};
